'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  Building2,
  Users,
  CalendarCheck,
  CreditCard,
  IdCard,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Settings,
  LayoutDashboard,
  UserCog,
  ScanLine,
  X,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { dashboardService, SidebarBadges } from '@/lib/services/dashboardService';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

function buildAdminNavGroups(badges: SidebarBadges | null) {
  return [
    {
      label: 'Overview',
      items: [
        { href: '/', label: 'Dashboard', icon: LayoutDashboard, badge: null },
      ],
    },
    {
      label: 'Operations',
      items: [
        { href: '/sites-management', label: 'Sites', icon: Building2, badge: null },
        { href: '/worker-registration', label: 'Workers', icon: Users, badge: badges && badges.workers > 0 ? String(badges.workers) : null },
        { href: '/attendance-management', label: 'Attendance', icon: CalendarCheck, badge: badges && badges.attendance > 0 ? String(badges.attendance) : null },
        { href: '/field-agent-portal', label: 'Field Scanner', icon: ScanLine, badge: null },
      ],
    },
    {
      label: 'Documents & Pay',
      items: [
        { href: '/work-id-card-generator', label: 'ID Cards', icon: IdCard, badge: null },
        { href: '/payment-tracking', label: 'Payments', icon: CreditCard, badge: badges && badges.payments > 0 ? String(badges.payments) : null },
      ],
    },
    {
      label: 'Admin',
      items: [
        { href: '/user-management', label: 'Field Users', icon: UserCog, badge: null },
      ],
    },
  ];
}

const fieldNavGroups = [
  {
    label: 'Field Operations',
    items: [
      { href: '/field-agent-portal', label: 'Attendance Scanner', icon: ScanLine, badge: null },
    ],
  },
];

export default function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onMobileClose }: SidebarProps) {
  const pathname = usePathname();
  const sidebarWidth = collapsed ? 'w-16' : 'w-60';

  return (
    <>
      {/* Mobile sidebar overlay + drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={onMobileClose}
        />
      )}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 w-60 bg-secondary flex flex-col lg:hidden
          transform transition-transform duration-300 ease-in-out
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <SidebarContent collapsed={false} pathname={pathname} onMobileClose={onMobileClose} />
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`
          ${sidebarWidth} sidebar-transition
          hidden lg:flex flex-col bg-secondary border-r border-secondary/20 h-full flex-shrink-0 relative
        `}
      >
        <SidebarContent collapsed={collapsed} pathname={pathname} onToggleCollapse={onToggleCollapse} />
      </aside>
    </>
  );
}

function SidebarContent({
  collapsed,
  pathname,
  onToggleCollapse,
  onMobileClose,
}: {
  collapsed: boolean;
  pathname: string;
  onToggleCollapse?: () => void;
  onMobileClose?: () => void;
}) {
  const { user, logout } = useAuth();
  const router = useRouter();
  const [badges, setBadges] = useState<SidebarBadges | null>(null);

  useEffect(() => {
    if (user?.role === 'admin') {
      dashboardService.getSidebarBadges().then(setBadges).catch(() => {});
    }
  }, [user?.role]);

  const navGroups = user?.role === 'field' ? fieldNavGroups : buildAdminNavGroups(badges);

  const handleSignOut = () => {
    logout();
    toast.success('Signed out successfully.');
    router.push('/sign-up-login-screen');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-white/10 ${collapsed ? 'justify-center' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-lg text-white tracking-tight">Decor Holdings</span>
        )}
        {onMobileClose && (
          <button onClick={onMobileClose} className="ml-auto p-1 text-white/60 hover:text-white">
            <X size={18} />
          </button>
        )}
      </div>

      {/* User info */}
      {!collapsed && user && (
        <div className="px-4 py-3 border-b border-white/10">
          <p className="text-xs font-600 text-white truncate">{user.name}</p>
          <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full mt-0.5 inline-block ${
            user.role === 'admin' ? 'bg-primary/20 text-primary' : 'bg-green-500/20 text-green-300'
          }`}>
            {user.role === 'admin' ? 'Administrator' : 'Field Agent'}
          </span>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-5">
        {navGroups.map((group) => (
          <div key={`group-${group.label}`}>
            {!collapsed && (
              <p className="px-2 mb-1.5 text-[11px] font-600 uppercase tracking-widest text-white/40">
                {group.label}
              </p>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const isActive = pathname === item.href;
                const NavIcon = item.icon;
                return (
                  <Link
                    key={`nav-${item.href}`}
                    href={item.href}
                    onClick={onMobileClose}
                    className={`
                      flex items-center gap-3 px-2 py-2.5 rounded-lg transition-all duration-150 group relative
                      ${isActive
                        ? 'bg-primary/20 text-primary' : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }
                      ${collapsed ? 'justify-center' : ''}
                    `}
                    title={collapsed ? item.label : undefined}
                  >
                    <NavIcon size={18} className="flex-shrink-0" />
                    {!collapsed && (
                      <span className="text-sm font-500 flex-1">{item.label}</span>
                    )}
                    {!collapsed && item.badge && (
                      <span className="text-[11px] font-600 bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full tabular-nums">
                        {item.badge}
                      </span>
                    )}
                    {collapsed && item.badge && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                    )}
                    {collapsed && (
                      <span className="
                        absolute left-full ml-2 px-2 py-1 bg-secondary-foreground text-secondary text-xs font-500
                        rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50
                        transition-opacity duration-150
                      ">
                        {item.label}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t border-white/10 p-2 space-y-0.5">
        {user?.role === 'admin' && (
          <button
            className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
            title={collapsed ? 'Settings' : undefined}
          >
            <Settings size={18} />
            {!collapsed && <span className="text-sm font-500">Settings</span>}
          </button>
        )}
        <button
          onClick={handleSignOut}
          className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-white/60 hover:bg-red-500/20 hover:text-red-300 transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Sign Out' : undefined}
        >
          <LogOut size={18} />
          {!collapsed && <span className="text-sm font-500">Sign Out</span>}
        </button>

        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-white/40 hover:bg-white/10 hover:text-white/70 transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          >
            {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            {!collapsed && <span className="text-sm font-500">Collapse</span>}
          </button>
        )}
      </div>
    </div>
  );
}